/**
 * Estado do editor.
 *
 * A timeline vive aqui, não no React Query: ela muda a 60 fps durante um arraste, e
 * passar isso por cache de query seria desastroso. A separação é deliberada — React
 * Query cuida do que vem do servidor e não muda a cada frame; este store cuida da edição.
 *
 * Comandos são aplicados LOCALMENTE primeiro (a UI responde em menos de um frame) e
 * enviados ao servidor em seguida. Se o servidor recusar, o inverso é aplicado e o
 * usuário vê o motivo — o otimismo é latência escondida, não uma aposta.
 */
import { create } from 'zustand'
import {
  applyCommand,
  canRedo,
  canUndo,
  commandLabel,
  createHistory,
  redo as redoHistory,
  sequenceDuration,
  undo as undoHistory,
  type Command,
  type History,
  type Sequence,
} from '@easyedit/timeline'
import { api, ApiError } from '../lib/api/client'

export type SelectionKind = 'items' | 'range' | 'none'

export interface EditorState {
  sequence: Sequence | null
  projectId: string | null
  history: History

  selectedItemIds: string[]
  selectionRange: { start: number; end: number } | null
  playhead: number
  isPlaying: boolean

  /** Pixels por segundo na timeline. */
  zoom: number
  scrollLeft: number
  snapEnabled: boolean
  rippleEnabled: boolean

  saving: boolean
  lastSavedRevision: number
  error: string | null
  /** Comandos aguardando confirmação do servidor. */
  pending: number
  /** Ids já aplicados — descarta o eco do próprio envio vindo pelo WebSocket. */
  appliedCommandIds: Set<string>

  // ações
  load: (sequence: Sequence, projectId: string) => void
  dispatch: (
    command: Command,
    options?: {
      local?: boolean
      /**
       * Junta este comando ao anterior de mesma chave, se for recente.
       *
       * É o que transforma um arraste de slider — que dispara dezenas de comandos — em
       * UM passo de undo e UM envio. Sem isso, desfazer um ajuste de opacidade exigiria
       * sessenta Ctrl+Z.
       */
      coalesceKey?: string
    },
  ) => Promise<void>
  undo: () => Promise<void>
  redo: () => Promise<void>
  applyRemote: (commands: Command[]) => void

  select: (itemIds: string[], additive?: boolean) => void
  clearSelection: () => void
  setRange: (range: { start: number; end: number } | null) => void
  setPlayhead: (time: number) => void
  setPlaying: (playing: boolean) => void
  setZoom: (zoom: number) => void
  setScrollLeft: (value: number) => void
  toggleSnap: () => void
  toggleRipple: () => void
  setError: (error: string | null) => void
}

/** Ids pedidos mais todos os irmãos de grupo deles. */
function expandGroups(sequence: Sequence, itemIds: string[]): string[] {
  const groups = new Set<string>()
  for (const track of sequence.tracks) {
    for (const item of track.items) {
      if (itemIds.includes(item.id) && item.groupId) groups.add(item.groupId)
    }
  }
  if (groups.size === 0) return itemIds
  const result = new Set(itemIds)
  for (const track of sequence.tracks) {
    for (const item of track.items) {
      if (item.groupId && groups.has(item.groupId)) result.add(item.id)
    }
  }
  return [...result]
}

/** Janela em que dois comandos de mesma chave viram um só passo de undo. */
const COALESCE_WINDOW_MS = 700

const MIN_ZOOM = 2
const MAX_ZOOM = 400

export const useEditorStore = create<EditorState>((set, get) => ({
  sequence: null,
  projectId: null,
  history: createHistory(),
  selectedItemIds: [],
  selectionRange: null,
  playhead: 0,
  isPlaying: false,
  zoom: 40,
  scrollLeft: 0,
  snapEnabled: true,
  rippleEnabled: false,
  saving: false,
  lastSavedRevision: 0,
  error: null,
  pending: 0,
  appliedCommandIds: new Set<string>(),

  load: (sequence, projectId) =>
    set({
      sequence,
      projectId,
      history: createHistory(),
      selectedItemIds: [],
      selectionRange: null,
      playhead: 0,
      lastSavedRevision: sequence.revision,
      error: null,
      pending: 0,
      appliedCommandIds: new Set<string>(),
    }),

  dispatch: async (command, options = {}) => {
    const state = get()
    const sequence = state.sequence
    if (!sequence) return

    let next: Sequence
    let inverse: Command
    try {
      const result = applyCommand(sequence, command)
      next = result.sequence
      inverse = result.inverse
    } catch (error) {
      // Rejeitado pelo próprio reducer: nem chega a sair da máquina.
      set({ error: error instanceof Error ? error.message : 'Operação inválida' })
      return
    }

    // Coalescência: a entrada anterior de mesma chave é SUBSTITUÍDA, mantendo o inverso
    // ORIGINAL. É o inverso do primeiro comando da série que devolve o valor de antes do
    // arraste — o inverso do último devolveria o penúltimo valor, e desfazer não
    // desfaria nada visível.
    const previous = state.history.undo[state.history.undo.length - 1]
    const merge =
      options.coalesceKey !== undefined &&
      previous?.coalesceKey === options.coalesceKey &&
      Date.now() - Date.parse(previous.at) < COALESCE_WINDOW_MS

    const entry = {
      command,
      inverse: merge ? previous!.inverse : inverse,
      label: commandLabel(command),
      at: new Date().toISOString(),
      coalesceKey: options.coalesceKey,
    }

    set({
      sequence: next,
      appliedCommandIds: trim(new Set(state.appliedCommandIds).add(command.id)),
      history: merge
        ? { ...state.history, undo: [...state.history.undo.slice(0, -1), entry] }
        : pushEntry(state.history, entry),
      error: null,
      pending: state.pending + 1,
      saving: true,
    })

    if (options.local) {
      set((s) => ({ pending: Math.max(0, s.pending - 1), saving: false }))
      return
    }

    await persist([command], sequence.id, set, get)
  },

  undo: async () => {
    const { sequence, history } = get()
    if (!sequence || !canUndo(history)) return
    const result = undoHistory(sequence, history)
    if (!result) return
    set({ sequence: result.sequence, history: result.history, saving: true, pending: get().pending + 1 })
    await persist([result.entry.inverse], sequence.id, set, get)
  },

  redo: async () => {
    const { sequence, history } = get()
    if (!sequence || !canRedo(history)) return
    const result = redoHistory(sequence, history)
    if (!result) return
    set({ sequence: result.sequence, history: result.history, saving: true, pending: get().pending + 1 })
    await persist([result.entry.command], sequence.id, set, get)
  },

  /**
   * Comandos vindos de outro cliente (ou de um job de IA) pelo WebSocket.
   *
   * Não entram no histórico local: desfazer a edição de outra pessoa por engano é pior
   * que não poder desfazê-la.
   *
   * O servidor faz broadcast para a sala inteira, inclusive para quem enviou. Sem
   * deduplicar por id, o cliente aplicaria o PRÓPRIO comando duas vezes — e um
   * DELETE_RANGE aplicado em dobro remove o dobro do vídeo.
   */
  applyRemote: (commands) => {
    const state = get()
    const sequence = state.sequence
    if (!sequence) return

    const fresh = commands.filter((c) => c.id && !state.appliedCommandIds.has(c.id))
    if (fresh.length === 0) return

    let current = sequence
    const applied = new Set(state.appliedCommandIds)
    for (const command of fresh) {
      try {
        current = applyCommand(current, command).sequence
        applied.add(command.id)
      } catch {
        // Um comando remoto que não aplica significa que o estado local divergiu;
        // recarregar a sequência é mais seguro que insistir.
        set({ error: 'A timeline mudou em outro lugar. Recarregue o projeto.' })
        return
      }
    }
    set({ sequence: current, lastSavedRevision: current.revision, appliedCommandIds: trim(applied) })
  },

  /**
   * Seleciona, expandindo grupos.
   *
   * É AQUI que o agrupamento ganha efeito, e não no reducer: selecionar um membro
   * seleciona os irmãos, e a partir daí mover, apagar, duplicar e aparar já operam sobre
   * o grupo inteiro sem que nenhuma dessas operações precise saber o que é um grupo.
   */
  select: (itemIds, additive = false) =>
    set((state) => {
      const expanded = state.sequence ? expandGroups(state.sequence, itemIds) : itemIds
      return {
        selectedItemIds: additive
          ? [...new Set([...state.selectedItemIds, ...expanded])]
          : expanded,
        selectionRange: null,
      }
    }),

  clearSelection: () => set({ selectedItemIds: [], selectionRange: null }),
  setRange: (range) => set({ selectionRange: range, selectedItemIds: [] }),
  setPlayhead: (time) => set({ playhead: Math.max(0, time) }),
  setPlaying: (playing) => set({ isPlaying: playing }),
  setZoom: (zoom) => set({ zoom: Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom)) }),
  setScrollLeft: (value) => set({ scrollLeft: Math.max(0, value) }),
  toggleSnap: () => set((s) => ({ snapEnabled: !s.snapEnabled })),
  toggleRipple: () => set((s) => ({ rippleEnabled: !s.rippleEnabled })),
  setError: (error) => set({ error }),
}))

function pushEntry(history: History, entry: History['undo'][number]): History {
  const undo = [...history.undo, entry]
  if (undo.length > history.limit) undo.shift()
  return { ...history, undo, redo: [] }
}

/**
 * Mantém o conjunto de ids limitado.
 *
 * Uma sessão longa acumularia milhares de ids; só interessam os recentes, porque o eco
 * do WebSocket chega em segundos, não em horas.
 */
const MAX_TRACKED_IDS = 500

function trim(ids: Set<string>): Set<string> {
  if (ids.size <= MAX_TRACKED_IDS) return ids
  const kept = [...ids].slice(-MAX_TRACKED_IDS)
  return new Set(kept)
}

/**
 * Fila de envio.
 *
 * O servidor aplica comandos sob trava otimista: cada POST exige a revisão que ele
 * espera. Dois comandos saindo em paralelo — dois sliders no mesmo tick, ou um arraste
 * rápido — fazem o segundo chegar com a revisão já vencida, e ele volta 409. O usuário
 * então lia "outra pessoa editou esta sequência", que era mentira: era ele mesmo, duas
 * vezes no mesmo instante, e a segunda edição era descartada.
 *
 * Serializar resolve os dois problemas de uma vez: nada de conflito falso, e o log de
 * comandos do servidor fica na mesma ordem em que a pessoa editou — que é o que o replay
 * e o versionamento dependem.
 */
let sendQueue: Promise<unknown> = Promise.resolve()

function enqueue<T>(task: () => Promise<T>): Promise<T> {
  const result = sendQueue.then(task, task)
  // A fila não pode parar por causa de um envio que falhou.
  sendQueue = result.catch(() => undefined)
  return result
}

async function persist(
  commands: Command[],
  sequenceId: string,
  set: (partial: Partial<EditorState>) => void,
  get: () => EditorState,
): Promise<void> {
  await enqueue(async () => {
    try {
      const result = await api.post<{ revision: number; duration: number }>(
        `/sequences/${sequenceId}/commands`,
        { commands },
      )
      set({ lastSavedRevision: result.revision })
    } catch (error) {
      const message =
        error instanceof ApiError
          ? error.status === 409
            ? 'Outra pessoa editou esta sequência. Recarregue para continuar.'
            : error.message
          : 'Não foi possível salvar'
      // O servidor recusou: desfaz localmente para que a tela não minta sobre o que
      // existe. A entrada revertida é a DESTE comando, encontrada por id — com a fila,
      // outras edições podem ter entrado no histórico enquanto este estava em trânsito.
      const state = get()
      const ids = new Set(commands.map((c) => c.id))
      const index = state.history.undo.findIndex((e) => ids.has(e.command.id))
      const entry = index >= 0 ? state.history.undo[index] : undefined
      if (entry && state.sequence) {
        try {
          const reverted = applyCommand(state.sequence, entry.inverse)
          set({
            sequence: reverted.sequence,
            history: {
              ...state.history,
              undo: state.history.undo.filter((_e, i) => i !== index),
            },
          })
        } catch {
          // se nem o inverso aplica, só recarregar resolve
        }
      }
      set({ error: message })
    } finally {
      set({ pending: Math.max(0, get().pending - 1), saving: get().pending > 1 })
    }
  })
}

/** Duração derivada — nunca armazenada, para não divergir. */
export function useDuration(): number {
  const sequence = useEditorStore((s) => s.sequence)
  return sequence ? sequenceDuration(sequence) : 0
}

/**
 * Handle do store no `window`, apenas em desenvolvimento.
 *
 * Os roteiros de aceitação dirigem o editor num navegador de verdade e precisam conferir
 * o ESTADO da timeline, não só o que está desenhado: um clip que parece ter mudado de
 * track sem ter mudado seria pior que um erro visível. Sem isto, a alternativa seria
 * espalhar atributos de teste pela interface inteira.
 *
 * O `import.meta.env.DEV` garante que não existe no build de produção.
 */
if (import.meta.env.DEV) {
  ;(window as unknown as { __editorStore?: typeof useEditorStore }).__editorStore = useEditorStore
}
